"use client";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import PaymentTable from "../../components/payment/payment-table";
import Header from "../../components/header/index";
import { LocalStorageService } from "../../helpers";
import ProfileService from "../../core/services/profile.service";
import { useTranslation } from "react-i18next";
import useConfigEnv from "../../zustand";

const PaymentDetail = ({ logo }: { logo: string }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const accessTokenExp = LocalStorageService.getAccessToken();
  const [userId, setUserId] = useState();
  const [userName, setUserName] = useState();
  const [outStanding, setOutStanding] = useState<any>();
  const [partnerId, setPartnerId] = useState();
  const studentId = searchParams.get("studentId") || "false";
  const applicationID = searchParams.get("applicationID") || "false";
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { env }: any = useConfigEnv();
  const lastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const api = env?.apiCall;
    const sid = userId ?? studentId;
    const tmpSid = applicationID ?? null;

    if (!api) return;

    const requestKey = JSON.stringify({ api, sid, tmpSid });

    // Nếu key y hệt lần trước → bỏ qua (ngăn StrictMode gọi 2 lần khi mount)
    if (lastKeyRef.current === requestKey) return;
    lastKeyRef.current = requestKey;

    const ac = new AbortController();
    setIsLoading(true);
    // KHÔNG setData(null) để tránh flicker; giữ data cũ cho đến khi có data mới
    (async () => {
      try {
        const response = await fetch(api, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          cache: "no-store",
          signal: ac.signal,
          body: JSON.stringify({
            model: "res.partner",
            method: "web_search_read",
            ids: [],
            kwargs: {
              specification: {
                stud_id: {},
                name: {},
                birthdate: {},
                image_1920: {},
                tmp_stud_id: {},
                total_amount_outstanding: {},
                total_available_refund: {}
              },
              offset: 0,
              order: "",
              limit: 1,
              domain: [
                "|",
                ["stud_id", "=", sid && sid != "false" ? sid : tmpSid],
                ["tmp_stud_id", "=", sid && sid != "false" ? sid : tmpSid],
              ],
            },
          }),
        });

        if (!response.ok) {
          // Không đè data cũ; chỉ log
          console.error("Fetch failed:", response.status, response.statusText);
          return;
        }

        const res = await response.json();
        const record =
          res?.records?.[0] ??
          res?.[0] ?? // phòng trường hợp API trả shape khác
          null;

        // Chỉ set nếu request chưa bị hủy
        if (!ac.signal.aborted) {
          setData(record);
        }
      } catch (err) {
        if ((err as any)?.name !== "AbortError") {
          console.error("Error fetching data:", err);
        }
        // Không xóa data cũ khi lỗi để tránh "nháy lỗi"
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    })();

    return () => {
      ac.abort(); // hủy request cũ khi deps đổi/unmount
    };
    // Quan trọng: chỉ theo dõi các primitive, KHÔNG để cả object env
  }, [env?.apiCall, studentId, userId, applicationID]);

  useEffect(() => {
    const fetchUserInfo = () => {
      ProfileService.getIdUser()
        .then((res: any) => {
          if (res?.partner_id?.stud_id || res?.partner_id?.tmp_stud_id) {
            setUserId(res?.partner_id?.stud_id);
            const newParams = new URLSearchParams(searchParams);
            newParams.set("applicationID", res?.partner_id?.tmp_stud_id);
            newParams.set("studentId", res?.partner_id?.stud_id);
            setSearchParams(newParams);
            setPartnerId(res?.partner_id?.id);
            setUserName(res?.name);
            setOutStanding(res?.total_amount_outstanding);
          } else {
            window.location.href = "/404page";
            LocalStorageService.clearToken();
          }
        })
        .catch((err) => {
          console.log(err);
        });
    };
    if (accessTokenExp) {
      fetchUserInfo();
    }
  }, [accessTokenExp]);

  if (isLoading) {
    return <div></div>;
  }

  return (
    <>
      <Header logo={logo} center={false} />

      <div className="flex w-full items-center justify-center">
        {data && ((userId && partnerId) || studentId || applicationID) ? (
          <div className="flex w-full max-w-[1440px] flex-col gap-8 px-2 py-8 sm:px-2 md:px-2 lg:px-2 xl:px-2 2xl:px-[100px]">
            <div className="flex w-full flex-col gap-6">
              <h3 className="text-4xl font-semibold leading-[39.6px] tracking-[-0.36px] text-text-black text-left">
                {t("tuition_information")}
              </h3>
              <div className="relative flex w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-xl border border-[#E2E4E9] bg-white p-2 shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] sm:flex-row md:flex-row lg:flex-row">
                <div className="absolute right-0 top-0 md:h-[165px] md:w-[165px] w-[80px] h-[80px]">
                  <img
                    src="/images/payment/logo-water-mark.svg"
                    alt="logo"
                    width={165}
                    height={165}
                  />
                </div>
                <div className="max-h-[154px] max-w-[154px] rounded-md border border-[#f1f1f1]">
                  <img
                    className="max-h-[154px] max-w-[154px]"
                    src={data?.image_1920 || "/images/payment/avt.png"}
                    alt="Avatar"
                    width={154}
                    height={154}
                  />
                </div>
                <div className="flex flex-col items-start justify-between gap-4 p-4 sm:h-[154px] sm:flex-1 sm:px-8 sm:py-[13px]">
                  <div className="flex w-full flex-row gap-2 sm:flex-row sm:items-center">
                    <span className="font-normal text-text-secondary">
                      <span className="font-normal text-text-secondary">
                        {studentId !== "false"
                          ? t("student_code")
                          : applicationID === "false"
                            ? t("student_code")
                            : "Application ID"}
                      </span>
                    </span>
                    <span className="font-semibold text-text-primary">
                      {userId
                        ? userId
                        : studentId && studentId !== "false"
                          ? studentId
                          : applicationID}
                    </span>
                  </div>
                  <div className="h-[1px] w-full flex-[none] bg-[#EFEFEF]"></div>
                  <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:gap-10">
                    <div className="flex w-full flex-row gap-2 sm:w-fit sm:flex-row sm:items-center">
                      <span className="font-normal text-text-secondary">
                        {t("fullname")}
                      </span>
                      <span className="font-semibold text-text-primary">
                        {userName ? userName : data?.name}
                      </span>
                    </div>
                  </div>
                  <div className="h-[1px] w-full flex-[none] bg-[#EFEFEF]"></div>
                  <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:gap-10">
                    <div className="flex w-full flex-row gap-2 sm:w-fit sm:flex-row sm:items-center">
                      <span className="font-normal text-text-secondary">
                        {t("balance_current")}
                      </span>
                      <span className="font-semibold text-text-primary">
                        {outStanding
                          ? outStanding.toLocaleString()
                          : data?.total_available_refund.toLocaleString()}{" "}
                        VND
                      </span>
                    </div>
                    {/* <div className="flex w-full flex-row gap-2 sm:w-fit sm:flex-row sm:items-center">
                    <span className="font-normal text-text-secondary">
                      Ngày sinh:
                    </span>
                    <span className="font-semibold text-text-primary">
                      {data?.birthdate}
                    </span>
                  </div> */}
                  </div>
                  <div className="block h-[1px] w-full flex-[none] bg-[#EFEFEF]"></div>
                </div>
              </div>
            </div>
            <div className="mb-5 rounded-xl border border-[#EBEBEB] shadow-[0px_4px_20px_0px_rgba(96,111,134,0.04)]">
              <PaymentTable
                studentId={userId ? userId : studentId}
                applicationID={userId ? userId : applicationID}
                partnerId={partnerId}
              />
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 flex justify-center items-center">
            <div className="w-[48px] h-[48px]">
              <img
                src="/images/payment/loading.png"
                alt="loading icon"
                width={48}
                height={48}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentDetail;
